import BLEEventDispatcher from "./BLEEventDispatcher";
import { GatewayClient } from "./gateway/client";
import { connectBle } from "./gateway/ble";
import { connectRemote, REMOTE_DEVICE_ID, REMOTE_TOKEN_KEY, REMOTE_URL } from "./gateway/remote";
import { handleBatteryPacket } from "./controllers/batteryController";
import { handleHeaterPacket } from "./controllers/heaterController";
import { handleHotWaterPacket } from "./controllers/hotWaterController";
import { handleLightsPacket } from "./controllers/lightsController";

let transport, renewal, connecting=false, generation=0, updateInterval=500;
let connectionType='ble',lastTelemetry=null,signalDbm=null;
let pendingWrites=0,progressTimer;
const rttSamples=[];
const ids=[0x183,0x283,0x184,0x284,0x384,0x484,0x504,0x185,0x285,0x385,0x485,0x355,0x356,0x35f,0x703,0x704,0x705];
const client=new GatewayClient((id,payload)=>magic.onCanFrame(id,payload),rtt=>magic.onGatewayTiming(rtt));
function report(error) {magic.bleEventDispatcher.emit('command-error',error.message);}
function beginWrite() {
	clearTimeout(progressTimer);
	pendingWrites++;
	magic.bleEventDispatcher.emit('command-progress','pending');
}
function finishWrite(success) {
	pendingWrites=Math.max(0,pendingWrites-1);
	if(pendingWrites)return;
	clearTimeout(progressTimer);
	magic.bleEventDispatcher.emit('command-progress',success?'success':'idle');
	if(success)progressTimer=setTimeout(()=>magic.bleEventDispatcher.emit('command-progress','idle'),650);
}
async function renew() {
  if(!transport)return;
	try {await client.subscribe(ids,updateInterval,document.hidden?0:60);}catch(e){report(e);}
}
const magic={
  bleEventDispatcher:new BLEEventDispatcher(),
	emitQuality() {
		const sorted=[...rttSamples].sort((a,b)=>a-b);
		const middle=Math.floor(sorted.length/2);
		const rtt=!sorted.length ? null : sorted.length%2 ? sorted[middle] : (sorted[middle-1]+sorted[middle])/2;
		magic.bleEventDispatcher.emit('connection-quality',{transport:connectionType,rtt,lastTelemetry,signalDbm});
	},
	onGatewayTiming(rtt) {
		rttSamples.push(rtt);
		if(rttSamples.length>5)rttSamples.shift();
		magic.emitQuality();
	},
  onCanFrame(id,payload) {
		lastTelemetry=Date.now();
		magic.emitQuality();
    magic.bleEventDispatcher.emit('raw',{id,payload,timestamp:Date.now()});
    magic.handlePacket(id,payload);
  },
	async connectUsing(openConnection, interval, type) {
    if(connecting)return false;
    connecting=true;
    try {
      await magic.disconnectBLE();
	connectionType=type;rttSamples.length=0;lastTelemetry=null;signalDbm=null;
      magic.bleEventDispatcher.emit('status','connecting');
      const session=++generation;
	const next=await openConnection(bytes=>client.feed(bytes),()=>{
        if(session!==generation)return;
        generation++;transport=null;clearInterval(renewal);client.close();
        magic.bleEventDispatcher.emit('status','disconnected');
      },stage=>magic.bleEventDispatcher.emit('status',stage));
      if(session!==generation){next.close();throw new Error('BLE connection cancelled');}
      transport=next;
	updateInterval=interval;
      client.attach(bytes=>next.send(bytes));
      magic.bleEventDispatcher.emit('status','Checking gateway');
      await client.hello();
	await client.subscribe(ids,updateInterval,document.hidden?0:60);
      if(session!==generation || transport!==next)throw new Error('BLE connection ended');
      renewal=setInterval(renew,20000);
      magic.bleEventDispatcher.emit('status','connected');
			return true;
		}catch(e){await magic.disconnectBLE();report(e);magic.bleEventDispatcher.emit('status',e.message);return false;}
    finally {connecting=false;}
  },
	connectBLE() {return magic.connectUsing(connectBle,500,'ble');},
	async connectInternet(password) {
		const savedToken=localStorage.getItem(REMOTE_TOKEN_KEY);
		const connected=await magic.connectUsing((onBytes,onDisconnect,onProgress)=>
			connectRemote({url:REMOTE_URL,deviceId:REMOTE_DEVICE_ID,token:savedToken,password},
				onBytes,onDisconnect,onProgress,token=>{
					if(token)localStorage.setItem(REMOTE_TOKEN_KEY,token);
					else localStorage.removeItem(REMOTE_TOKEN_KEY);
					magic.bleEventDispatcher.emit('internet-token',Boolean(token));
				},status=>{signalDbm=status.signalDbm;magic.emitQuality();}),1000,'internet');
		return connected;
	},
	hasInternetAccess() {return Boolean(localStorage.getItem(REMOTE_TOKEN_KEY));},
	forgetInternetAccess() {
		localStorage.removeItem(REMOTE_TOKEN_KEY);
		magic.bleEventDispatcher.emit('internet-token',false);
	},
	async disconnectBLE() {
    generation++;clearInterval(renewal);client.close();
    const old=transport;transport=null;old?.close();
    magic.bleEventDispatcher.emit('status','disconnected');
  },
  async readSDO(node,index,sub=0) {
    try{return await client.readSDO(node,index,sub);}catch(e){report(e);throw e;}
  },
  async writeSDO(node,index,sub,data) {
		beginWrite();
		try {const result=await client.writeSDO(node,index,sub,data);finishWrite(true);return result;}
		catch(e){finishWrite(false);report(e);throw e;}
  },
  // Raw CAN injection is deliberately not part of the new gateway protocol.
  async sendCanFrame() {throw new Error('Use gateway SDO reads/writes; raw CAN sending is unavailable');},
	battery: {
		voltage: 0,
		current: 0,
		power: 0,
		temp: 0,
		soc: 0,
		soh: 0,
		status: "Idle",
		capacity: 0,
	},
	lights: {
		white: [0, 0, 0],
		timer: 0,
		color: [0, 0, 0],
		button: false,
		ledBar: 0,
	},
	heater: {
		pump_hz: 0,
		target_pump_hz: 0,
		target_power: 8,
		running_state: 0,
		error_state: 0,
		heat_exchanger_temp: 0,
		supply_voltage: 0,
		fan_rpm: 0,
		temp: null,
		ideal_temp: 0,
		redundant_count: 0,
		pump_counter: 0,
		heater_timer: 0,
		climate_control_enabled: 0,
		uptime: 0,
	},
	hotWater: {
		underfloor_pump: 0,
		radiator_pump: 0,
		big_pump: 0,
		fill_valve: 0,
		flush_valve: 0,
		led1_state: 0,
		tank1_state: 0,
		water_level: 0,
		autoheat: 0,
		heatfloor: 0,
		temp: 0,
		target_temp: 0,
		autofill_level: 0,
		uptime: 0,
		// Immersion heater properties
		immersion_heater_state: 0,
		immersion_timer_remaining: 0,
		// Battery heating properties
		battery_data_fresh: 0,
		soc_threshold: 98,
		soc_timer_remaining: 0, // hours
		watt_soc_threshold: 90,
		watt_timer_remaining: 0, // hours
	},
	handlePacket: (id, payload) => {
		if (handleBatteryPacket(id, payload, magic.battery, magic.bleEventDispatcher)) return;
		if (handleHeaterPacket(id, payload, magic.heater, magic.bleEventDispatcher)) return;
		if (handleHotWaterPacket(id, payload, magic.hotWater, magic.bleEventDispatcher)) return;
		if (handleLightsPacket(id, payload, magic.lights, magic.bleEventDispatcher)) return;
	},
};

if(typeof document!=='undefined')document.addEventListener('visibilitychange',renew);
export default magic;
